import styled from "@emotion/styled"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Empty,
  Image,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd"
import noImage from "assets/images/no-image.png"
import { useMemo, useState } from "react"
import {
  useDistributeDepartmentStock,
  useGetFactoryStore,
  useListDepartmentMaster,
  useLotBatchCodesWithStock,
} from "hooks/useFactoryDepartmentStock"
import {
  FactoryDepartmentDistributeLine,
  FactoryDepartmentStockLineItem,
  InventoryStockData,
  LotBatchCodeWithStock,
} from "services/interface"

interface DistributeStockModalProps {
  open: boolean
  onClose: () => void
}

interface UILineItem extends FactoryDepartmentStockLineItem {
  uid: string
  label?: string
  image?: string | null
  available?: number
}

interface UILine {
  uid: string
  department_master_id?: string
  items: UILineItem[]
  // ตัวเลือกชั่วคราวสำหรับเพิ่มสินค้า (เลือกสินค้า -> เลือก SKU เหมือนหน้า admin)
  draftProductId?: string
  draftSkuId?: string
}

// 1 SKU ในคลังต้นทาง
interface ProductSkuOption {
  product_sku_id: string
  sku_name: string
  qty_onhand: number
}

// 1 สินค้า (รวมหลาย SKU)
interface ProductGroup {
  product_id: string
  product_name: string
  image: string | null
  qty_onhand: number
  skus: ProductSkuOption[]
}

const makeUid = () =>
  `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

const DistributeStockModal = ({ open, onClose }: DistributeStockModalProps) => {
  const [remark, setRemark] = useState<string>("")
  const [lines, setLines] = useState<UILine[]>([
    { uid: makeUid(), department_master_id: undefined, items: [] },
  ])

  const { data: masterData, isLoading: loadingMaster } =
    useListDepartmentMaster()
  const { data: factoryStoreData, isLoading: loadingStore } =
    useGetFactoryStore()
  const distributeMutation = useDistributeDepartmentStock()

  const departments = masterData?.data ?? []
  const allWarehouses = factoryStoreData?.data.warehouses ?? []
  const centralWarehouse = allWarehouses.find(
    (w) => w.warehouse.department_master_id === null
  )

  const sourceWarehouse = centralWarehouse

  // จัดกลุ่มสต็อกของคลังต้นทางเป็น สินค้า -> SKU (เหมือน loadAllProducts ของ admin)
  const productGroups = useMemo<ProductGroup[]>(() => {
    if (!sourceWarehouse) return []
    const map = new Map<string, ProductGroup>()
    sourceWarehouse.stocks.forEach((s) => {
      if ((s.qty_onhand ?? 0) <= 0) return
      const productId = s.product_id
      if (!map.has(productId)) {
        map.set(productId, {
          product_id: productId,
          product_name:
            s.product_name || s.product?.name || productId,
          image: s.product_image || s.product?.image || null,
          qty_onhand: 0,
          skus: [],
        })
      }
      const group = map.get(productId)!
      group.qty_onhand += s.qty_onhand ?? 0
      const skuId = s.product_sku_id || ""
      const existingSku = group.skus.find((k) => k.product_sku_id === skuId)
      if (existingSku) {
        existingSku.qty_onhand += s.qty_onhand ?? 0
      } else {
        group.skus.push({
          product_sku_id: skuId,
          sku_name:
            s.product_sku_name ||
            s.product_sku?.warehouse_sku_name ||
            "(ไม่มี SKU)",
          qty_onhand: s.qty_onhand ?? 0,
        })
      }
    })
    return Array.from(map.values())
  }, [sourceWarehouse])

  const reset = () => {
    setRemark("")
    setLines([{ uid: makeUid(), department_master_id: undefined, items: [] }])
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { uid: makeUid(), department_master_id: undefined, items: [] },
    ])
  }

  const removeLine = (uid: string) => {
    setLines((prev) => prev.filter((l) => l.uid !== uid))
  }

  const setLineDept = (uid: string, deptId: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.uid === uid ? { ...l, department_master_id: deptId } : l
      )
    )
  }

  const setLineDraftProduct = (uid: string, productId?: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.uid === uid
          ? { ...l, draftProductId: productId, draftSkuId: undefined }
          : l
      )
    )
  }

  const setLineDraftSku = (uid: string, skuId?: string) => {
    setLines((prev) =>
      prev.map((l) => (l.uid === uid ? { ...l, draftSkuId: skuId } : l))
    )
  }

  const addDraftItem = (uid: string) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.uid !== uid) return l
        if (!l.draftProductId || l.draftSkuId === undefined) return l

        const group = productGroups.find(
          (p) => p.product_id === l.draftProductId
        )
        if (!group) return l
        const sku = group.skus.find(
          (k) => k.product_sku_id === l.draftSkuId
        )
        if (!sku) return l

        const exists = l.items.some(
          (it) =>
            it.product_id === group.product_id &&
            (it.product_sku_id || "") === sku.product_sku_id
        )
        if (exists) {
          message.warning("สินค้า/SKU นี้ถูกเพิ่มในรายการแล้ว")
          return l
        }

        const label = [group.product_name, sku.sku_name]
          .filter(Boolean)
          .join(" / ")

        const newItem: UILineItem = {
          uid: makeUid(),
          product_id: group.product_id,
          product_sku_id: sku.product_sku_id || undefined,
          inventory_product_id: group.product_id,
          qty: 1,
          label,
          image: group.image,
          available: undefined,
        }
        return {
          ...l,
          items: [...l.items, newItem],
          draftProductId: undefined,
          draftSkuId: undefined,
        }
      })
    )
  }

  const updateItemQty = (lineUid: string, itemUid: string, qty: number) => {
    setLines((prev) =>
      prev.map((l) =>
        l.uid === lineUid
          ? {
              ...l,
              items: l.items.map((it) =>
                it.uid === itemUid ? { ...it, qty } : it
              ),
            }
          : l
      )
    )
  }

  const updateItemLot = (
    lineUid: string,
    itemUid: string,
    lot: LotBatchCodeWithStock
  ) => {
    setLines((prev) =>
      prev.map((l) =>
        l.uid === lineUid
          ? {
              ...l,
              items: l.items.map((it) =>
                it.uid === itemUid
                  ? {
                      ...it,
                      lot_code: lot.lot_code || undefined,
                      batch_code: lot.batch_code || undefined,
                      lot_date: lot.lot_date || undefined,
                      expire_date: lot.expire_date || undefined,
                      mfg_date: lot.mfg_date || undefined,
                      available: lot.remaining_qty,
                      qty: Math.min(
                        Math.max(it.qty, 1),
                        lot.remaining_qty || 1
                      ),
                    }
                  : it
              ),
            }
          : l
      )
    )
  }

  const removeItem = (lineUid: string, itemUid: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.uid === lineUid
          ? { ...l, items: l.items.filter((it) => it.uid !== itemUid) }
          : l
      )
    )
  }

  const handleSubmit = () => {
    const linesWithDeptAndItems = lines.filter(
      (l) => l.department_master_id && l.items.length > 0
    )

    if (linesWithDeptAndItems.length === 0) {
      message.warning("กรุณาเลือกแผนกและสินค้าที่ต้องการโอนสินค้า")
      return
    }

    const hasMissingLot = linesWithDeptAndItems.some((l) =>
      l.items.some((it) => !it.lot_code)
    )
    if (hasMissingLot) {
      message.warning("กรุณาเลือก lot ให้ครบทุกสินค้า")
      return
    }

    const hasInvalidQty = linesWithDeptAndItems.some((l) =>
      l.items.some((it) => !(it.qty > 0))
    )
    if (hasInvalidQty) {
      message.warning("กรุณาระบุจำนวนให้ถูกต้อง")
      return
    }

    const validLines: FactoryDepartmentDistributeLine[] =
      linesWithDeptAndItems.map((l) => ({
        department_master_id: String(l.department_master_id),
        items: l.items.map((it) => ({
          product_id: it.product_id,
          product_sku_id: it.product_sku_id,
          inventory_product_id: it.inventory_product_id,
          qty: it.qty,
          lot_code: it.lot_code,
          batch_code: it.batch_code,
          lot_date: it.lot_date,
          expire_date: it.expire_date,
          mfg_date: it.mfg_date,
        })),
      }))

    distributeMutation.mutate(
      {
        from_warehouse_id: centralWarehouse?.warehouse.id,
        lines: validLines,
        remark: remark || undefined,
      },
      {
        onSuccess: (res) => {
          message.success(res.data.message || "การโอนสินค้าสำเร็จ")
          handleClose()
        },
        onError: () => {
          message.error("การโอนสินค้าไม่สำเร็จ")
        },
      }
    )
  }

  return (
    <Modal
      title="การโอนสินค้าไปยังแผนก"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="ยืนยันการโอนสินค้า"
      cancelText="ยกเลิก"
      confirmLoading={distributeMutation.isPending}
      width={800}
      destroyOnClose
    >
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Label>คลังต้นทาง</Label>
          <LockedWarehouseBox>
            {loadingStore ? (
              <Typography.Text type="secondary">กำลังโหลด...</Typography.Text>
            ) : centralWarehouse ? (
              <>
                <Tag color="geekblue">คลังกลาง</Tag>
                <Typography.Text strong>
                  {centralWarehouse.warehouse.name}
                </Typography.Text>
              </>
            ) : (
              <Typography.Text type="danger">ไม่พบคลังกลาง</Typography.Text>
            )}
          </LockedWarehouseBox>
        </Col>
        <Col xs={24} md={12}>
          <Label>หมายเหตุ</Label>
          <Input
            value={remark}
            placeholder="หมายเหตุ (ไม่บังคับ)"
            onChange={(e) => setRemark(e.target.value)}
          />
        </Col>
      </Row>

      <SectionTitle>รายการการโอนสินค้า</SectionTitle>

      {lines.length === 0 && (
        <Empty
          description="ยังไม่มีรายการ"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {lines.map((line, idx) => {
        const draftGroup = productGroups.find(
          (p) => p.product_id === line.draftProductId
        )
        return (
          <LineBox key={line.uid}>
            <LineHeader>
              <Space>
                <Tag color="purple">รายการที่ {idx + 1}</Tag>
                <Select
                  placeholder="เลือกแผนกปลายทาง"
                  loading={loadingMaster}
                  style={{ minWidth: 220 }}
                  value={line.department_master_id}
                  onChange={(v) => setLineDept(line.uid, v)}
                  showSearch
                  optionFilterProp="label"
                  options={departments.map((d) => ({
                    value: String(d.id),
                    label: d.description,
                  }))}
                />
              </Space>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => removeLine(line.uid)}
                disabled={lines.length === 1}
              >
                ลบ
              </Button>
            </LineHeader>

            {/* เลือกสินค้า -> เลือก SKU -> เพิ่ม (เหมือนหน้า admin) */}
            <AddItemRow>
              <Select
                placeholder="เลือกสินค้า"
                showSearch
                style={{ flex: 2, minWidth: 220 }}
                value={line.draftProductId}
                optionFilterProp="title"
                onChange={(v) => setLineDraftProduct(line.uid, v)}
                notFoundContent={
                  sourceWarehouse
                    ? "ไม่มีสต็อกในคลังต้นทาง"
                    : "ไม่พบคลังต้นทาง"
                }
                options={productGroups.map((p) => ({
                  value: p.product_id,
                  title: p.product_name,
                  label: (
                    <Space>
                      <Image
                        src={p.image || noImage}
                        fallback={noImage}
                        width={28}
                        height={28}
                        preview={false}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                      />
                      <span>{p.product_name}</span>
                    </Space>
                  ),
                }))}
              />
              <Select
                placeholder="เลือก SKU"
                showSearch
                style={{ flex: 1, minWidth: 160 }}
                value={line.draftSkuId}
                optionFilterProp="label"
                disabled={!line.draftProductId}
                onChange={(v) => setLineDraftSku(line.uid, v)}
                notFoundContent="ไม่มี SKU"
                options={(draftGroup?.skus ?? []).map((k) => ({
                  value: k.product_sku_id,
                  label: `${k.sku_name} (คงเหลือ ${k.qty_onhand})`,
                }))}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={
                  !line.draftProductId || line.draftSkuId === undefined
                }
                onClick={() => addDraftItem(line.uid)}
              >
                เพิ่ม
              </Button>
            </AddItemRow>

            {line.items.length === 0 && (
              <Typography.Text type="secondary">
                ยังไม่มีสินค้าในรายการนี้
              </Typography.Text>
            )}

            {line.items.map((item) => (
              <DistributeItemCard
                key={item.uid}
                item={item}
                warehouseId={centralWarehouse?.warehouse.id}
                onChangeLot={(itemUid, lot) =>
                  updateItemLot(line.uid, itemUid, lot)
                }
                onChangeQty={(itemUid, qty) =>
                  updateItemQty(line.uid, itemUid, qty)
                }
                onRemove={(itemUid) => removeItem(line.uid, itemUid)}
              />
            ))}
          </LineBox>
        )
      })}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        block
        onClick={addLine}
        style={{ marginTop: 8 }}
      >
        เพิ่มรายการการโอนสินค้าไปแผนกใหม่
      </Button>
    </Modal>
  )
}

export default DistributeStockModal

const buildLotLabel = (r: LotBatchCodeWithStock) => {
  const exp = r.expire_date
    ? new Date(r.expire_date).toLocaleDateString("th-TH")
    : null
  const parts = [
    `Lot ${r.lot_code}`,
    `คงเหลือ ${r.remaining_qty}`,
    exp ? `EXP ${exp}` : null,
  ].filter(Boolean)
  return parts.join(" · ")
}

interface DistributeItemCardProps {
  item: UILineItem
  warehouseId?: string
  onChangeLot: (itemUid: string, lot: LotBatchCodeWithStock) => void
  onChangeQty: (itemUid: string, qty: number) => void
  onRemove: (itemUid: string) => void
}

const DistributeItemCard = ({
  item,
  warehouseId,
  onChangeLot,
  onChangeQty,
  onRemove,
}: DistributeItemCardProps) => {
  // ไม่ส่ง sku_variant_id (เหมือน admin) เพื่อให้ remaining_qty ของ lot ถูกต้อง
  const { data, isLoading } = useLotBatchCodesWithStock(
    warehouseId && item.product_id
      ? {
          warehouse_id: warehouseId,
          product_id: item.product_id,
          product_sku_id: item.product_sku_id,
        }
      : null
  )

  const lotOptions = useMemo(() => {
    const rows = (data?.data ?? []).filter((r) => !!r.lot_code)
    return rows.map((r) => ({
      value: r.lot_code as string,
      label: buildLotLabel(r),
      lot: r,
    }))
  }, [data])

  return (
    <ItemCard>
      <Image
        src={item.image || noImage}
        fallback={noImage}
        width={48}
        height={48}
        style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
        preview={{ src: item.image || noImage }}
      />
      <ItemMain>
        <Typography.Text strong>{item.label}</Typography.Text>
        <Select
          placeholder="เลือก lot (บังคับ)"
          loading={isLoading}
          size="small"
          style={{ width: "100%", marginTop: 4 }}
          value={item.lot_code}
          showSearch
          optionFilterProp="label"
          status={item.lot_code ? undefined : "warning"}
          onChange={(v) => {
            const opt = lotOptions.find((o) => o.value === v)
            if (opt) onChangeLot(item.uid, opt.lot)
          }}
          options={lotOptions.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          notFoundContent={
            isLoading ? "กำลังโหลด..." : "ไม่มี lot ที่มีสต็อกในคลังต้นทาง"
          }
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          คงเหลือ lot: {item.available ?? "-"}
        </Typography.Text>
      </ItemMain>
      <ItemActions>
        <InputNumber
          min={1}
          max={item.available || undefined}
          value={item.qty}
          disabled={!item.lot_code}
          onChange={(v) => onChangeQty(item.uid, Number(v) || 1)}
          style={{ width: 110 }}
          addonAfter="หน่วย"
        />
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => onRemove(item.uid)}
        />
      </ItemActions>
    </ItemCard>
  )
}

const Label = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`

const LockedWarehouseBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 11px;
  min-height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background-color: #f5f5f5;
`

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-top: 16px;
  margin-bottom: 8px;
`

const LineBox = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background-color: #fafafa;
`

const LineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`

const AddItemRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`

const ItemCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #f0f0f0;
  background-color: #ffffff;
  border-radius: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 8px;
`

const ItemMain = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
